import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { EmojiIcon, ListIcon, TextSizeIcon } from '@treams/treams-icons';
import {
	Card,
	Highlight,
	Paragraph,
	PlainInput,
	Popover,
	Tags,
	WorkingArea,
} from '@treams/treams-ui';
import useLocalizedState from 'packages/kakapo-ui-application/hooks/useLocalizedState';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import * as graphql from 'generated/graphql';

export default function EngagementQuestionCard(props: {
	item: graphql.EngagementQuestion & { category: graphql.EngagementSubcategory };
	types: graphql.EngagementType[];
	new?: boolean;
	query?: string;
	selected?: boolean;
	readOnly?: boolean;
	buttons?: ReactNode;
	onCancel?: () => Promise<void> | void;
	onCreate?: (item: graphql.EngagementQuestion) => void;
	onUpdate?: (item: graphql.EngagementQuestion) => void;
	onSelect?: (item: graphql.EngagementQuestion) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [editing, setEditing] = useState(props.new ?? false);
	const [autoFocus, setAutoFocus] = useState('text');
	const [title, setTitle] = useState(props.item.title);
	const [localizedTitle, setLocalizedTitle] = useLocalizedState(title, setTitle);
	const [typeID, setTypeID] = useState(props.item.type.id);
	const [tooltip, setTooltip] = useState(false);

	const type = props.types.find(({ id }) => id === typeID)!;

	const [create] = graphql.useCreateEngagementQuestionMutation();
	const [update] = graphql.useUpdateEngagementQuestionMutation();

	const onSaveHandler = async () => {
		if (props.new) {
			const { data } = await create({
				variables: { input: { subcategoryID: props.item.category.id, title, typeID } },
			});

			if (props.onCreate && data) {
				props.onCreate(data.createEngagementQuestion.question as graphql.EngagementQuestion);
			}
		} else {
			const { data } = await update({
				variables: { input: { id: props.item.id, title } },
			});

			if (props.onUpdate && data) {
				props.onUpdate(data.updateEngagementQuestion.question as graphql.EngagementQuestion);
			}
		}

		setEditing(false);
	};

	return (
		<WorkingArea
			active={editing}
			onCancel={() => {
				setTitle(props.item.title);
				setTypeID(props.item.type.id);
				props.onCancel?.();
				setEditing(false);
			}}
			onSave={() => onSaveHandler()}
		>
			{(active) => (
				<Card
					selected={active || props.selected}
					onClick={props.onSelect && (() => props.onSelect?.(props.item))}
					flexGrow={1}
					flexShrink={1}
					banner={
						{
							[graphql.EngagementResponseType.Face]: 'yellow',
							[graphql.EngagementResponseType.Open]: 'employee',
							[graphql.EngagementResponseType.PickOne]: 'purple',
						}[type.type]
					}
				>
					<Paragraph>
						{props.readOnly ? (
							localizedTitle
						) : active ? (
							<PlainInput
								multiline
								onChange={setLocalizedTitle}
								onFocus={() => {
									setAutoFocus('text');
									setEditing(true);
								}}
								autoFocus={active && autoFocus === 'text'}
								value={localizedTitle}
							/>
						) : (
							<StyledBox
								width="24vw"
								onMouseDown={() => {
									setAutoFocus('text');
									setEditing(true);
								}}
							>
								<Highlight query={props.query}>{localizedTitle ?? ''}</Highlight>
							</StyledBox>
						)}
					</Paragraph>
					<Flex mt={2}>
						<Popover.Target
							contents={
								<Box p={3}>
									<Paragraph width={300}>
										{t('settings.engagementLibrary.question.type.readonly')}
									</Paragraph>
								</Box>
							}
							visible={tooltip}
							onDismiss={() => setTooltip(false)}
						>
							<Tags.Item.Select
								enabled={active && !!props.new}
								search={false}
								selectedValue={typeID}
								autoFocus={autoFocus === 'type'}
								onClick={
									props.readOnly
										? undefined
										: props.new
										? () => {
												setAutoFocus('type');
												setEditing(true);
										  }
										: () => {
												setTooltip(true);
										  }
								}
								onChange={(id) => setTypeID(id!)}
								options={props.types.map((type) => ({
									icon: {
										[graphql.EngagementResponseType.Open]: <TextSizeIcon />,
										[graphql.EngagementResponseType.PickOne]: <ListIcon />,
										[graphql.EngagementResponseType.Face]: <EmojiIcon />,
									}[type.type],
									title: localize(type.title).text,
									value: type.id,
								}))}
							/>
						</Popover.Target>
						{props.buttons}
					</Flex>
				</Card>
			)}
		</WorkingArea>
	);
}

const StyledBox = styled(Box)`
	overflow-wrap: break-word;
`;
